import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import RefinementChips, { RefinementAxis, Refinements } from './RefinementChips'

type FollowUpKey =
  | 'treatment'
  | 'non_pharm'
  | 'resources'
  | 'history'
  | 'exam'
  | 'billing'
  | 'referrals'

const FOLLOW_UP_LABELS: Record<FollowUpKey, string> = {
  treatment: 'More on treatment',
  non_pharm: 'Non-pharm options',
  resources: 'Patient resources',
  history: 'History',
  exam: 'Exam',
  billing: 'Billing (PBS / MBS)',
  referrals: 'Referrals',
}

interface DemoResponse {
  answer: string
  intent?: string
  follow_ups?: FollowUpKey[]
  refinement_axes?: RefinementAxis[]
  needs_clarification?: boolean
  clarification_options?: string[]
  primary_source?: string
  queries_used: number
  queries_max: number
  queries_remaining: number
  source_attribution: string
}

export default function DemoWidget() {
  const [query, setQuery] = useState('')
  const [lastQuery, setLastQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [response, setResponse] = useState<DemoResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [limitReached, setLimitReached] = useState(false)
  const [queryCount, setQueryCount] = useState(0)
  const [activeRefinements, setActiveRefinements] = useState<Refinements>({})

  const MAX_QUERIES = 2

  const submitQuery = async (q: string, refinements: Refinements = {}) => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetch(`https://api.gpmeta.au/demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: q.trim(),
          refinements: Object.keys(refinements).length > 0 ? refinements : undefined,
        })
      })

      if (!res.ok) {
        const errorData = await res.json() as { error?: string; message?: string; queries_used?: number; queries_max?: number }
        if (res.status === 429 || errorData.error === 'LIMIT_REACHED' || errorData.error === 'DEMO_AT_CAPACITY') {
          setLimitReached(true)
          if (typeof errorData.queries_used === 'number') setQueryCount(errorData.queries_used)
          return
        }
        throw new Error(errorData.message || 'Query failed')
      }

      const data: DemoResponse = await res.json()
      setResponse(data)
      setLastQuery(q.trim())
      setQueryCount(data.queries_used)
      setActiveRefinements(refinements)
      if (data.queries_remaining <= 0) setLimitReached(true)
      setQuery('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim() || queryCount >= MAX_QUERIES) return
    setActiveRefinements({})
    await submitQuery(query, {})
  }

  // Demo: follow-up chip click → signup CTA (each chip = a paid query)
  const handleFollowUp = (_key: FollowUpKey) => {
    window.location.href = '/login?signup=true'
  }

  // Demo: refinement clicks consume a real query if budget remains,
  // otherwise gate to signup. This lets the user feel the refinement
  // mechanic at least once before hitting the cap.
  const handleRefine = (axisKey: string, option: string) => {
    if (queryCount >= MAX_QUERIES || !lastQuery) {
      window.location.href = '/login?signup=true'
      return
    }
    const next = { ...activeRefinements, [axisKey]: option }
    submitQuery(lastQuery, next)
  }

  const handleClearRefinement = (axisKey: string) => {
    if (queryCount >= MAX_QUERIES || !lastQuery) {
      window.location.href = '/login?signup=true'
      return
    }
    const next = { ...activeRefinements }
    delete next[axisKey]
    submitQuery(lastQuery, next)
  }

  // Clarifier chip → re-issue query with the option appended (consumes a query).
  // If demo cap reached, gate to signup.
  const handleClarify = (option: string) => {
    if (queryCount >= MAX_QUERIES || !lastQuery) {
      window.location.href = '/login?signup=true'
      return
    }
    setActiveRefinements({})
    submitQuery(`${lastQuery} ${option}`, {})
  }

  const showSignupPrompt = limitReached || queryCount >= MAX_QUERIES

  if (showSignupPrompt) {
    return (
      <div className="bg-white rounded-lg border-2 border-blue-200 p-8 text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-3">
          Demo limit reached
        </h3>
        <p className="text-gray-600 mb-6">
          Sign up for free access during beta — full source filtering and Australian guideline coverage.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="/login?signup=true"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Sign up free
          </a>
          <a
            href="/login"
            className="bg-white text-blue-700 border border-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
          >
            Log in
          </a>
        </div>
      </div>
    )
  }

  const followUps = response?.follow_ups ?? []
  const refinementAxes = response?.refinement_axes ?? []
  const showRefinements = refinementAxes.length > 0 || Object.keys(activeRefinements).length > 0

  return (
    <div className="bg-white rounded-lg border-2 border-blue-200 p-6 max-w-4xl mx-auto text-left">
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Try: 'Chlamydia treatment first line'"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
            disabled={isLoading}
            maxLength={200}
          />
          <button
            type="submit"
            disabled={isLoading || !query.trim() || queryCount >= MAX_QUERIES}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {response && (
        <div className="border-t border-gray-200 pt-4 text-left space-y-4">
          {/* Clarifier: chip-only, no preamble. Each chip re-issues. */}
          {response.needs_clarification && (response.clarification_options?.length ?? 0) > 0 ? (
            <div className="flex flex-wrap gap-2">
              {response.clarification_options!.map((opt) => (
                <button
                  key={opt}
                  onClick={() => handleClarify(opt)}
                  disabled={isLoading}
                  className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors border border-blue-200 disabled:opacity-50"
                >
                  {opt}
                </button>
              ))}
            </div>
          ) : (
            /* Pyramid answer — plain, no Summary header */
            <div className="prose prose-sm max-w-none text-gray-900">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <p className="text-gray-900 leading-relaxed mb-3">{children}</p>,
                  h2: ({ children }) => <p className="text-gray-900 leading-relaxed mb-3">{children}</p>,
                  h3: ({ children }) => <p className="text-gray-900 leading-relaxed mb-3">{children}</p>,
                  p: ({ children }) => <p className="text-gray-900 leading-relaxed mb-3">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc pl-5 text-gray-900 mb-3 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal pl-5 text-gray-900 mb-3 space-y-1">{children}</ol>,
                  li: ({ children }) => <li className="text-gray-900">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                }}
              >
                {response.answer}
              </ReactMarkdown>
            </div>
          )}

          {/* Refinement axes — narrow the answer (consumes a query) */}
          {!response.needs_clarification && showRefinements && (
            <RefinementChips
              axes={refinementAxes}
              active={activeRefinements}
              onSelect={handleRefine}
              onClear={handleClearRefinement}
              disabled={isLoading}
            />
          )}

          {/* Learn more — chips gate to signup in demo */}
          {!response.needs_clarification && followUps.length > 0 && (
            <div>
              <span className="text-xs uppercase tracking-wide text-gray-500 mr-3">Learn more:</span>
              <span className="inline-flex flex-wrap gap-2 align-middle">
                {followUps.map((key) => (
                  <button
                    key={key}
                    onClick={() => handleFollowUp(key)}
                    className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200"
                    title="Sign up free to follow up"
                  >
                    {FOLLOW_UP_LABELS[key]}
                  </button>
                ))}
              </span>
            </div>
          )}

          {/* Source attribution */}
          {response.source_attribution && (
            <p className="text-xs text-gray-500">{response.source_attribution}</p>
          )}

          {response.queries_remaining === 1 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>1 query left.</strong>{' '}
                <a href="/login?signup=true" className="font-semibold text-amber-900 underline hover:text-amber-700">
                  Sign up
                </a>{' '}
                for more.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
